import {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import './StageRulerFrame.css';

const RULER_THICKNESS = 22;

/** Ekranda yaklaşık 56px major aralık için mantıklı adım (sahne koordinatı, px). */
function rulerStep(zoom: number): number {
  const target = 56 / Math.max(zoom, 0.01);
  const pow10 = Math.pow(10, Math.floor(Math.log10(Math.max(target, 1e-6))));
  const n = target / pow10;
  const f = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return f * pow10;
}

function buildTicksAlongRuler(length: number, originOffset: number, step: number) {
  const minor = step / 5;
  const ticks: { pos: number; scene: number; major: boolean }[] = [];
  const kMin = Math.floor((0 - originOffset) / minor - 1e-6);
  const kMax = Math.ceil((length - originOffset) / minor + 1e-6);
  for (let k = kMin; k <= kMax; k++) {
    const scene = k * minor;
    const pos = originOffset + scene;
    if (pos < -0.5 || pos > length + 0.5) continue;
    const major = k % 5 === 0;
    ticks.push({ pos, scene, major });
  }
  return ticks;
}

interface StageRulerFrameProps {
  zoom: number;
  children: ReactNode;
}

export const StageRulerFrame = forwardRef<HTMLDivElement, StageRulerFrameProps>(function StageRulerFrame(
  { zoom, children },
  scrollContainerRef,
) {
  const shellRef = useRef<HTMLDivElement>(null);
  const topCellRef = useRef<HTMLDivElement>(null);
  const leftCellRef = useRef<HTMLDivElement>(null);

  const [layout, setLayout] = useState<{
    wHoriz: number;
    hVert: number;
    offsetX: number;
    offsetY: number;
  }>({ wHoriz: 0, hVert: 0, offsetX: 0, offsetY: 0 });

  const step = useMemo(() => rulerStep(zoom), [zoom]);

  const measure = useCallback(() => {
    const topCell = topCellRef.current;
    const leftCell = leftCellRef.current;
    const scrollEl =
      scrollContainerRef && 'current' in scrollContainerRef
        ? scrollContainerRef.current
        : null;
    const stage = scrollEl?.querySelector('.stage') as HTMLElement | null | undefined;
    if (!topCell || !leftCell || !scrollEl || !stage) return;

    const topRect = topCell.getBoundingClientRect();
    const leftRect = leftCell.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();

    setLayout({
      wHoriz: topRect.width,
      hVert: leftRect.height,
      offsetX: stageRect.left - topRect.left,
      offsetY: stageRect.top - leftRect.top,
    });
  }, [scrollContainerRef]);

  useLayoutEffect(() => {
    measure();
    const shell = shellRef.current;
    const scrollEl =
      scrollContainerRef && 'current' in scrollContainerRef
        ? scrollContainerRef.current
        : null;
    const ro = new ResizeObserver(() => measure());
    if (shell) ro.observe(shell);
    if (topCellRef.current) ro.observe(topCellRef.current);
    if (leftCellRef.current) ro.observe(leftCellRef.current);
    if (scrollEl) ro.observe(scrollEl);

    scrollEl?.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);

    return () => {
      ro.disconnect();
      scrollEl?.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [measure, scrollContainerRef, zoom]);

  const { wHoriz, hVert, offsetX, offsetY } = layout;

  const hTicks = useMemo(
    () => (wHoriz > 0 ? buildTicksAlongRuler(wHoriz, offsetX, step) : []),
    [wHoriz, offsetX, step],
  );
  const vTicks = useMemo(
    () => (hVert > 0 ? buildTicksAlongRuler(hVert, offsetY, step) : []),
    [hVert, offsetY, step],
  );

  const T = RULER_THICKNESS;

  return (
    <div
      ref={shellRef}
      className="stage-ruler-shell"
      style={
        {
          '--ruler-thickness': `${T}px`,
        } as React.CSSProperties
      }
    >
      <div className="stage-ruler-row stage-ruler-row--top">
        <div className="stage-ruler-corner" aria-hidden />
        <div ref={topCellRef} className="stage-ruler-cell stage-ruler-cell--h">
          {wHoriz > 0 && (
            <svg
              className="stage-ruler-svg stage-ruler-svg--horizontal"
              width={wHoriz}
              height={T}
              viewBox={`0 0 ${wHoriz} ${T}`}
              preserveAspectRatio="none"
            >
              <rect x={0} y={0} width={wHoriz} height={T} className="stage-ruler-bg" />
              {hTicks.map(({ pos, major }) => (
                <line
                  key={`ht-${pos}`}
                  x1={pos}
                  y1={T}
                  x2={pos}
                  y2={major ? T - 10 : T - 5}
                  className={major ? 'stage-ruler-line-major' : 'stage-ruler-line-minor'}
                />
              ))}
              {hTicks
                .filter(({ major }) => major)
                .map(({ pos, scene }) => (
                  <text key={`hl-${pos}`} x={pos + 2} y={12} className="stage-ruler-label" style={{ fontSize: 10 }}>
                    {Math.round(scene)}
                  </text>
                ))}
            </svg>
          )}
        </div>
        <div className="stage-ruler-corner" aria-hidden />
      </div>

      <div className="stage-ruler-row stage-ruler-row--mid">
        <div ref={leftCellRef} className="stage-ruler-cell stage-ruler-cell--v">
          {hVert > 0 && (
            <svg
              className="stage-ruler-svg stage-ruler-svg--vertical"
              width={T}
              height={hVert}
              viewBox={`0 0 ${T} ${hVert}`}
              preserveAspectRatio="none"
            >
              <rect x={0} y={0} width={T} height={hVert} className="stage-ruler-bg" />
              {vTicks.map(({ pos, major }) => (
                <line
                  key={`vt-${pos}`}
                  x1={T}
                  y1={pos}
                  x2={major ? T - 10 : T - 5}
                  y2={pos}
                  className={major ? 'stage-ruler-line-major' : 'stage-ruler-line-minor'}
                />
              ))}
              {vTicks
                .filter(({ major }) => major)
                .map(({ pos, scene }) => (
                  <text
                    key={`vl-${pos}`}
                    x={8}
                    y={pos - 2}
                    className="stage-ruler-label stage-ruler-label--rotated"
                    transform={`rotate(-90, 8, ${pos})`}
                    style={{ fontSize: 10 }}
                  >
                    {Math.round(scene)}
                  </text>
                ))}
            </svg>
          )}
        </div>

        <div ref={scrollContainerRef} className="stage-ruler-center">
          <div className="stage-ruler-center-inner">{children}</div>
        </div>

        <div className="stage-ruler-cell stage-ruler-cell--v">
          {hVert > 0 && (
            <svg
              className="stage-ruler-svg stage-ruler-svg--vertical"
              width={T}
              height={hVert}
              viewBox={`0 0 ${T} ${hVert}`}
              preserveAspectRatio="none"
            >
              <rect x={0} y={0} width={T} height={hVert} className="stage-ruler-bg" />
              {vTicks.map(({ pos, major }) => (
                <line
                  key={`vrt-${pos}`}
                  x1={0}
                  y1={pos}
                  x2={major ? 10 : 5}
                  y2={pos}
                  className={major ? 'stage-ruler-line-major' : 'stage-ruler-line-minor'}
                />
              ))}
              {vTicks
                .filter(({ major }) => major)
                .map(({ pos, scene }) => (
                  <text
                    key={`vrl-${pos}`}
                    x={14}
                    y={pos - 2}
                    className="stage-ruler-label stage-ruler-label--rotated"
                    transform={`rotate(-90, 14, ${pos})`}
                    style={{ fontSize: 10 }}
                  >
                    {Math.round(scene)}
                  </text>
                ))}
            </svg>
          )}
        </div>
      </div>

      <div className="stage-ruler-row stage-ruler-row--bottom">
        <div className="stage-ruler-corner" aria-hidden />
        <div className="stage-ruler-cell stage-ruler-cell--h">
          {wHoriz > 0 && (
            <svg
              className="stage-ruler-svg stage-ruler-svg--horizontal"
              width={wHoriz}
              height={T}
              viewBox={`0 0 ${wHoriz} ${T}`}
              preserveAspectRatio="none"
            >
              <rect x={0} y={0} width={wHoriz} height={T} className="stage-ruler-bg" />
              {hTicks.map(({ pos, major }) => (
                <line
                  key={`hb-${pos}`}
                  x1={pos}
                  y1={0}
                  x2={pos}
                  y2={major ? 10 : 5}
                  className={major ? 'stage-ruler-line-major' : 'stage-ruler-line-minor'}
                />
              ))}
              {hTicks
                .filter(({ major }) => major)
                .map(({ pos, scene }) => (
                  <text key={`hbl-${pos}`} x={pos + 2} y={14} className="stage-ruler-label" style={{ fontSize: 10 }}>
                    {Math.round(scene)}
                  </text>
                ))}
            </svg>
          )}
        </div>
        <div className="stage-ruler-corner" aria-hidden />
      </div>
    </div>
  );
});
